import { useEffect, useRef } from "react";

interface ConfettiProps {
  /** Fire the confetti burst when this becomes true. */
  active: boolean;
  /** Number of confetti pieces. */
  count?: number;
  /** How long the animation runs, in ms. */
  duration?: number;
}

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rot: number;
  vrot: number;
  color: string;
}

// BCH-flavored greens plus a few celebratory accents.
const COLORS = [
  "#22c55e",
  "#10b981",
  "#34d399",
  "#84cc16",
  "#facc15",
  "#f97316",
];

/**
 * A lightweight, dependency-free confetti burst rendered on a full-screen
 * canvas overlay. Respects prefers-reduced-motion. Purely decorative, so it's
 * marked aria-hidden and ignores pointer events.
 */
export function Confetti({ active, count = 160, duration = 2600 }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!active) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    let width = window.innerWidth;
    let height = window.innerHeight;

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas!.width = width * dpr;
      canvas!.height = height * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);

    // Launch pieces from the top, spread across the width.
    const pieces: Piece[] = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: -20 - Math.random() * height * 0.3,
      vx: (Math.random() - 0.5) * 3,
      vy: 2 + Math.random() * 4,
      size: 5 + Math.random() * 7,
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    }));

    const start = performance.now();
    let raf = 0;

    function frame(now: number) {
      const elapsed = now - start;
      const gravity = 0.06;
      ctx!.clearRect(0, 0, width, height);

      // Fade the pieces out over the final third of the animation.
      const fade =
        elapsed > duration * 0.66
          ? Math.max(0, 1 - (elapsed - duration * 0.66) / (duration * 0.34))
          : 1;

      for (const p of pieces) {
        p.vy += gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;

        ctx!.save();
        ctx!.globalAlpha = fade;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.rot);
        ctx!.fillStyle = p.color;
        ctx!.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx!.restore();
      }

      if (elapsed < duration) {
        raf = requestAnimationFrame(frame);
      } else {
        ctx!.clearRect(0, 0, width, height);
      }
    }

    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      ctx.clearRect(0, 0, width, height);
    };
  }, [active, count, duration]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[60]"
    />
  );
}
